import React, { useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Hidden React Component - Renders all QR codes using QRCodeSVG
 * Optimized for 300 DPI print quality at 2cm × 2cm (20mm × 20mm)
 * Configurable error correction level for different document styles
 * Matches reference sample: sample_bulk_qr_labels.pdf (4×4 grid, 16 labels per page)
 *
 * Purpose:
 * - Renders QRCodeSVG components for bulk PDF generation
 * - Uses the same React component that works perfectly for single QR codes
 * - Matches the exact layout/styling of single QR code print preview
 * - Renders invisibly in DOM for capture via html2canvas at 300 DPI
 * - Provides callback when all QRs are rendered
 * - QR tokens are base64-encoded (no encryption needed)
 *
 * Layout Structure (matches DeviceQRCode.js print layout):
 * 1. Device ID at top (device_identifier)
 * 2. Perfect square QRCodeSVG (size 240px optimized for 300 DPI @ 20mm / 2cm)
 * 3. Device info below:
 *    - Equipment: {device_name}
 *    - Dept: {department}
 *    - Model: {model}
 *    - SN: {serial_number}
 *
 * DPI Optimization:
 * - QR size 240px = 20mm (2cm) at 300 DPI (20 × 11.811 pixels/mm = 236.22px)
 * - When captured at 3.125x scale: 240px → 750px (maintains 300 DPI in PDF)
 * - SVG rendering at 240px ensures crisp, scalable output
 *
 * Why this works:
 * - QRCodeSVG renders perfect squares when in proper React context
 * - html2canvas captures these SVG elements as perfect square PNGs at any scale
 * - Matches single QR code rendering exactly
 * - Maintains resolution during 300 DPI capture
 *
 * @param {Array} bulkQRTokens - Array of QR token objects with device info (token = base64 encoded org_id|device_identifier, qr_url = https://yourdomain.com/device?token=...)
 * @param {Boolean} isVisible - Whether to render the component (show/hide)
 * @param {Function} onRenderComplete - Callback when all QRs are rendered
 * @param {String} errorCorrectionLevel - Error correction level: 'L'|'M'|'Q'|'H' (default: 'H')
 *   - 'L': Low (7% redundancy) - Cleanest appearance, most white space
 *   - 'M': Medium (15% redundancy) - Balanced, matches sample documents
 *   - 'Q': Quartile (25% redundancy) - High redundancy, denser
 *   - 'H': High (30% redundancy) - Most robust, very dense (default)
 * @returns {React.ReactNode} Hidden component with QR codes
 */
function QRBatchRenderer({ bulkQRTokens, isVisible, onRenderComplete, errorCorrectionLevel = 'H' }) {
  const qrRefsRef = useRef({});

  // Reset refs when the chunk of tokens changes
  useEffect(() => {
    qrRefsRef.current = {};
  }, [bulkQRTokens]);

  // Track rendering completion
  useEffect(() => {
    if (!bulkQRTokens || bulkQRTokens.length === 0 || !isVisible) {
      return;
    }

    // Set a timeout to allow React to render all components
    const renderTimer = setTimeout(() => {
      // Check if all SVG elements are rendered in DOM
      const allRendered = bulkQRTokens.every((_, idx) => {
        const ref = qrRefsRef.current[idx];
        return ref && ref.current && ref.current.querySelector('svg');
      });

      if (allRendered) {
        if (onRenderComplete) {
          onRenderComplete();
        }
      } else {
        console.warn(`⚠️ QRBatchRenderer: Not all QR codes rendered yet (${bulkQRTokens.length} expected)`);
        if (onRenderComplete) {
          onRenderComplete();
        }
      }
    }, 300); // Wait 300ms for React rendering

    return () => clearTimeout(renderTimer);
  }, [bulkQRTokens, isVisible, onRenderComplete]);

  if (!isVisible || !bulkQRTokens || bulkQRTokens.length === 0) {
    return null;
  }

  return (
    <div
      id="qr-batch-renderer"
      style={{
        position: 'fixed',
        left: '0',
        top: '0',
        opacity: 0,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        padding: '0px',
        background: 'white',
        zIndex: -1,
        width: 'auto'
      }}
    >
      {bulkQRTokens.map((token, idx) => {
        // Initialize ref for this QR code if it doesn't exist
        if (!qrRefsRef.current[idx]) {
          qrRefsRef.current[idx] = React.createRef();
        }

        return (
          <div
            key={`qr-batch-${idx}`}
            ref={qrRefsRef.current[idx]}
            className="qr-batch-card"
            style={{
              // Inline styles replicate .qr-print-layout from print CSS
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: '0.4rem',
              padding: '0.3rem',
              width: '159px',
              border: '1px solid #ddd',
              background: 'white',
              textAlign: 'center'
            }}
          >
            {/* Device ID - Inline styles replicate .qr-print-device-id from print CSS */}
            <div
              style={{
                fontSize: '0.7rem',
                fontWeight: '700',
                color: '#2c3e50',
                marginBottom: '-0.5rem',
                padding: '0.3rem 0.2rem',
                borderRadius: '3px',
                letterSpacing: '1px',
                fontFamily: "'Courier New', monospace",
                textAlign: 'center',
                width: '100%',
                lineHeight: '1'
              }}
            >
              {token.device_identifier}
            </div>

            {/* QR Code SVG - 240px generated, displayed at 20mm (2cm) for print - MATCHES SAMPLE DOCUMENT */}
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '0.2rem', marginBottom: '0.2rem' }}>
              <div style={{ width: '20mm', height: '20mm', aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <QRCodeSVG
                  id={`qr-batch-code-${idx}`}
                  value={token.qr_url}
                  size={240}
                  level={errorCorrectionLevel}
                  includeMargin={false}
                  style={{
                    // Inline styles replicate .qr-print-layout .qr-code-svg from print CSS
                    boxShadow: 'none',
                    borderRadius: '0px',
                    background: 'white',
                    padding: '0px',
                    width: '100%',
                    height: '100%',
                    display: 'block'
                  }}
                />
              </div>
            </div>

            {/* Device Info - table columns (reliable for preview + html2canvas PDF capture) */}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                fontSize: '0.55rem',
                lineHeight: '1.15',
                textAlign: 'left',
                margin: 0,
                padding: 0
              }}
            >
              <colgroup>
                <col style={{ width: '62px' }} />
                <col />
              </colgroup>
              <tbody>
                {[
                  { label: 'Equipment', value: token.device_name || 'Unknown' },
                  { label: 'Dept', value: token.department || 'N/A' },
                  { label: 'Model', value: token.model || 'N/A' },
                  { label: 'SN', value: token.serial_number || 'N/A' }
                ].map((row) => (
                  <tr key={row.label}>
                    <td
                      style={{
                        fontWeight: 700,
                        color: '#2c3e50',
                        verticalAlign: 'top',
                        whiteSpace: 'nowrap',
                        padding: '0 4px 1px 2px',
                        textAlign: 'left'
                      }}
                    >
                      {row.label}:
                    </td>
                    <td
                      style={{
                        color: '#495057',
                        verticalAlign: 'top',
                        padding: '0 2px 1px 0',
                        textAlign: 'left',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        whiteSpace: 'normal'
                      }}
                    >
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export default QRBatchRenderer;
